/**
 * «Задания» (§2.5): «Мои» и «Свободные». «Взять» ведёт прямо в карточку
 * байка — жалоба «нажимаю взять, а где он?» решается переходом, а не
 * дополнительным текстом.
 *
 * Сортировка пула по близости — на клиенте. Бэкенд отдаёт пул по убыванию
 * даты создания без гео (fleet/internal/data/maintenance_repo.go), поэтому
 * расстояние считаем здесь по координатам байка, которые уже приходят в
 * задаче. Срез в 100 задач бэкенд делает ДО сортировки, так что при большом
 * пуле ближайшая задача может в него не попасть — известный разрыв, не
 * решаемый на клиенте.
 */
import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  completionFor,
  scoutTaskTypeLabel,
  taskDisplayNumber,
  taskReasonText,
  telemetryState,
  useClaimTask,
  useCompleteTask,
  selectActive,
  useMyTasks,
  useTaskPool,
  type ScoutTask,
} from '@/api/scout'
import { useAuth } from '@/lib/auth'
import { distanceM, formatDistance, useMyPosition } from '@/lib/geo'
import { navUrl } from '@/lib/navigation'
import { OutboxPanel } from '@/components/outbox-panel'
import { AlertsSection } from '@/components/alerts-section'
import { useOutboxEntries } from '@/lib/offline/use-outbox-entries'

/** Подпись про заряд и связь. Разводит «сервер молчит» и «байк молчит». */
function telemetryLabel(task: ScoutTask): string {
  switch (telemetryState(task)) {
    case 'unknown':
      return 'Заряд — · данных нет'
    case 'stale':
      return 'Заряд — · байк не на связи'
    case 'fresh':
      return `Заряд ${task.batteryPct} % · ${task.locked ? 'закрыт' : 'открыт'}`
  }
}

function TaskCard({
  task,
  distance,
  action,
}: {
  task: ScoutTask
  distance: number | null
  action: React.ReactNode
}) {
  const number = taskDisplayNumber(task)
  return (
    <li className="rounded-xl border border-white/10 bg-white/5 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {/* Номер — крупнее типа задачи: приехав на место, скаут сверяет
              именно его. */}
          <Link
            to={`/bike/${task.vehicleId}`}
            className="font-mono text-lg font-bold underline-offset-4 hover:underline"
          >
            {number || 'Байк неизвестен'}
          </Link>
          <p className="mt-0.5 text-sm opacity-70">{scoutTaskTypeLabel[task.type] ?? task.type}</p>
          <p className="mt-1 text-sm opacity-60">{telemetryLabel(task)}</p>
        </div>
        {distance !== null ? (
          <span className="shrink-0 text-sm opacity-70">{formatDistance(distance)}</span>
        ) : null}
      </div>

      {taskReasonText(task) ? <p className="mt-2 text-sm opacity-80">{taskReasonText(task)}</p> : null}
      {task.address ? <p className="mt-0.5 text-sm opacity-60">{task.address}</p> : null}

      <div className="mt-3 flex items-center gap-2">
        {action}
        {task.lat || task.lng ? (
          <a
            href={navUrl(task)}
            target="_blank"
            rel="noreferrer"
            className="rounded-lg border border-white/15 px-3 py-2 text-sm"
          >
            Маршрут
          </a>
        ) : null}
      </div>
    </li>
  )
}

export default function TasksPage() {
  const navigate = useNavigate()
  const { logout, isPartner, canCollect } = useAuth()
  const pool = useTaskPool()
  const mine = useMyTasks()
  const claim = useClaimTask()
  const complete = useCompleteTask()
  const me = useMyPosition()
  // `?? []` — бейджу «не отправлено» безразличен лишний кадр с нулём.
  const pending = useOutboxEntries() ?? []

  const active = useMemo(() => selectActive(mine.data?.tasks ?? []), [mine.data])

  const sortedPool = useMemo(() => {
    const tasks = pool.data?.tasks ?? []
    if (!me) return tasks
    return [...tasks]
      .map((t) => ({ t, d: t.lat || t.lng ? distanceM(me, t) : Number.POSITIVE_INFINITY }))
      .sort((a, b) => a.d - b.d)
      .map((x) => x.t)
  }, [pool.data, me])

  return (
    <main className="mx-auto w-full max-w-lg p-4 pb-tabs">
      <header className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">Задания</h1>
        <div className="flex items-center gap-3">
          {/* Staff/admin — проверить режим сборщика его глазами. */}
          {canCollect ? (
            <Link to="/collector" className="text-sm opacity-60 underline">
              Режим сборщика
            </Link>
          ) : null}
          <button className="text-sm opacity-60 underline" onClick={() => void logout()}>
            Выйти
          </button>
        </div>
      </header>

      <OutboxPanel entries={pending} />

      {/*
        Тревоги — сверху, отдельной секцией, а не пятой вкладкой (владелец:
        держать вкладок не больше пяти). Партнёру не показываем — тот же
        гейт, что у «Свободные» ниже.
      */}
      {isPartner ? null : <AlertsSection />}

      {claim.isError || complete.isError ? (
        <p role="alert" className="mb-4 rounded-lg bg-red-500/15 px-4 py-3 text-sm text-red-300">
          Действие не сохранено на устройстве. Проверьте, что браузеру разрешено
          хранить данные, и повторите.
        </p>
      ) : null}

      <section className="mb-6">
        <h2 className="mb-2 text-sm font-medium uppercase tracking-wide opacity-50">Мои</h2>
        {mine.isLoading ? (
          <p className="text-sm opacity-60">Загружаем…</p>
        ) : active.length === 0 ? (
          <p className="text-sm opacity-60">
            {isPartner
              ? 'Найдите байк по номеру и нажмите «Переставить»'
              : 'Пусто. Пометьте байк «Забрать» в карточке — задача сразу окажется здесь.'}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {active.map((t) => {
              const completion = completionFor(t, me)
              return (
                <TaskCard
                  key={t.id}
                  task={t}
                  distance={me && (t.lat || t.lng) ? distanceM(me, t) : null}
                  action={
                    <button
                      className="rounded-lg bg-emerald-500 px-3 py-2 text-sm font-medium
                        text-black disabled:opacity-40"
                      disabled={completion === null}
                      onClick={() => {
                        if (completion) complete.mutate({ taskId: t.id, completion })
                      }}
                    >
                      Выполнено
                    </button>
                  }
                />
              )
            })}
          </ul>
        )}
      </section>

      {isPartner ? null : (
        <section>
          <h2 className="mb-2 text-sm font-medium uppercase tracking-wide opacity-50">
            Свободные {me ? '· ближайшие сверху' : ''}
          </h2>
          {pool.isLoading ? (
            <p className="text-sm opacity-60">Загружаем…</p>
          ) : sortedPool.length === 0 ? (
            <p className="text-sm opacity-60">Диспетчер пока ничего не назначил</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {sortedPool.map((t) => (
                <TaskCard
                  key={t.id}
                  task={t}
                  distance={me && (t.lat || t.lng) ? distanceM(me, t) : null}
                  action={
                    <button
                      className="rounded-lg border border-emerald-400/40 px-3 py-2 text-sm
                        font-medium text-emerald-300"
                      onClick={() => {
                        // «Взять» ведёт прямо в карточку: скаут должен сразу
                        // увидеть, где велик (§2.5).
                        claim.mutate(t.id, {
                          onSuccess: () => void navigate(`/bike/${t.vehicleId}`),
                        })
                      }}
                    >
                      Взять
                    </button>
                  }
                />
              ))}
            </ul>
          )}
        </section>
      )}
    </main>
  )
}
